import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Anchor, Button, Card, Center, Stack, Text } from '@mantine/core'
import { useState, type ReactNode } from 'react'

import { useRemoteConfig } from '../api/RemoteConfigContext'
import { LoadError } from '../components/LoadError'
import { PageSkeleton } from '../components/PageSkeleton'
import { notifyError } from '../lib/notify'
import { HEALTH_DATA_CONSENT_VERSION, profileItem, withHealthDataConsent } from '../sync/settings'
import { useSettings } from '../sync/useSettings'
import { usePush } from '../sync/useSync'
import { useAuth } from './AuthContext'

/**
 * Blocks the signed-in app until the account has recorded health-data consent (SETTINGS `profile.healthDataConsent`,
 * owner decision 27 Oct 2026): a separate, unticked step shown once after sign-in, same wording as Android. Neither
 * action is a default: Agree records consent on the profile; Decline signs back out rather than entering the app
 * without it.
 */
export function ConsentGate(props: { children: ReactNode }) {
  const { query, profile } = useSettings()
  const auth = useAuth()
  const push = usePush()
  const config = useRemoteConfig()
  const [busy, setBusy] = useState<'agree' | 'decline' | null>(null)
  // Exact wording the owner approved (same on Android), 27 Oct 2026.
  const consentText = t`Store my health information on Macrotide's servers. Macrotide stores your diary, weight and body measurements, progress photos, workouts and anything imported from Health Connect on its servers, to sync your devices and run your coaching. You can withdraw any time by deleting your account.`

  if (query.isPending) return <PageSkeleton cards={1} />
  if (query.isError) return <LoadError what="your settings" error={query.error} />
  // Also re-asked when the owner bumps `/config`'s `consentVersion` from the ops panel, without a release.
  if (profile?.healthDataConsent && profile.healthDataConsent.version >= config.consentVersion) return <>{props.children}</>

  const agree = () => {
    setBusy('agree')
    push.mutate([profileItem(withHealthDataConsent(profile, Math.max(HEALTH_DATA_CONSENT_VERSION, config.consentVersion)))], {
      onError: (error) => {
        notifyError(error)
        setBusy(null)
      },
    })
  }

  const decline = () => {
    setBusy('decline')
    void auth.signOut()
  }

  return (
    <Center mih="100vh" p="md">
      <Card padding="lg" maw={480} w="100%">
        <Stack gap="md">
          <Text fw={600}>
            <Trans>Before you continue</Trans>
          </Text>
          <Text size="sm">{consentText}</Text>
          {/* What declining costs, stated plainly so the choice is informed (owner, 29 Oct 2026): facts, no pressure. */}
          <Text size="sm">
            <Trans>
              If you don&apos;t agree, you&apos;re signed out: the web app can only show data that&apos;s stored on our servers. The phone
              app still works on its own in guest mode (logging, coaching and charts), without a backup or AI logging.
            </Trans>
          </Text>
          <Stack gap="xs">
            {/* The button's label is the explicit consent itself (owner, 29 Oct 2026; the phone's ConsentGateText.AGREE). */}
            <Button onClick={agree} loading={busy === 'agree'} disabled={busy !== null} fullWidth>
              <Trans>I agree: store my health data</Trans>
            </Button>
            <Button variant="subtle" color="gray" onClick={decline} loading={busy === 'decline'} disabled={busy !== null} fullWidth>
              <Trans>Decline and sign out</Trans>
            </Button>
          </Stack>
          <Text size="xs" c="dimmed">
            <Trans>
              Read the <Anchor href="/privacy" target="_blank" rel="noreferrer">privacy policy</Anchor>.
            </Trans>
          </Text>
        </Stack>
      </Card>
    </Center>
  )
}
